import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, Send, CheckCircle2, TrendingUp, ArrowUpRight, ArrowRight, Brain, FileText, UserPlus, BarChart3 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { clientsApi, statsApi } from '../lib/api';

const Stat = ({ label, value, change, icon: Icon }) => (
  <div className="relative border border-stone-200/80 bg-[#ffffff] p-6 rounded-sm hover:border-stone-300 transition-colors">
    <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-[#4b5320] to-transparent"></div>
    <div className="flex items-start justify-between mb-4">
      <div className="text-xs uppercase tracking-widest text-stone-500">{label}</div>
      <Icon className="w-4 h-4 text-[#4b5320]" strokeWidth={1.8} />
    </div>
    <div className="font-display text-3xl text-stone-900">{value}</div>
    {change && (
      <div className="flex items-center gap-1 text-xs text-[#7fb685] mt-2">
        <ArrowUpRight className="w-3 h-3" />
        {change}
      </div>
    )}
  </div>
);

const StatusBadge = ({ status }) => {
  const map = {
    active: { label: 'Active', cls: 'border-[#7fb685]/40 bg-[#7fb685]/10 text-[#7fb685]' },
    pending: { label: 'Pending', cls: 'border-stone-300 bg-stone-100 text-stone-700' },
    review: { label: 'Review', cls: 'border-[#4b5320]/40 bg-[#4b5320]/10 text-[#4b5320]' },
    completed: { label: 'Complete', cls: 'border-stone-300 bg-stone-100 text-stone-700' },
  };
  const s = map[status] || map.pending;
  return <div className={`text-xs uppercase tracking-widest px-2.5 py-1 rounded-sm border ${s.cls}`}>{s.label}</div>;
};

const Dashboard = () => {
  const [stats, setStats] = useState({ totalClients: 0, activeClients: 0, disputesInProgress: 0, itemsRemoved: 0, avgScoreIncrease: 0 });
  const [clients, setClients] = useState([]);
  const [activity, setActivity] = useState([]);

  useEffect(() => {
    statsApi.stats().then(r => setStats(r.data)).catch(() => {});
    statsApi.activity().then(r => setActivity(r.data)).catch(() => {});
    clientsApi.list().then(r => setClients(r.data.slice(0, 5))).catch(() => {});
  }, []);

  const iconMap = {
    dispute_sent: Send,
    item_removed: CheckCircle2,
    document_uploaded: FileText,
    client_added: UserPlus,
    analysis_complete: Brain,
  };

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#4b5320] mb-2">Mission Control</div>
          <h1 className="font-display text-4xl text-stone-900">Dashboard</h1>
          <p className="text-sm text-stone-700 mt-2">Snapshot of your dispute operations.</p>
        </div>
        <Link to="/app/clients">
          <Button className="bg-[#4b5320] hover:bg-[#3d4419] text-white font-semibold rounded-sm">
            <UserPlus className="w-4 h-4 mr-2" /> Add Client
          </Button>
        </Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Stat label="Total Clients" value={stats.totalClients} icon={Users} />
        <Stat label="Active Disputes" value={stats.disputesInProgress} icon={Send} />
        <Stat label="Items Removed" value={stats.itemsRemoved} icon={CheckCircle2} />
        <Stat label="Avg Score Lift" value={`+${stats.avgScoreIncrease}`} icon={TrendingUp} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 border border-stone-200/80 bg-[#ffffff] rounded-sm">
          <div className="flex items-center justify-between p-6 border-b border-stone-200/80">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#4b5320] mb-1">Roster</div>
              <h2 className="font-display text-xl text-stone-900">Recent Clients</h2>
            </div>
            <Link to="/app/clients" className="text-sm text-stone-700 hover:text-stone-900 flex items-center gap-1">
              View all <ArrowRight className="w-3 h-3" />
            </Link>
          </div>
          <div className="divide-y divide-stone-200/80">
            {clients.length === 0 && (
              <div className="p-10 text-center text-stone-500 text-sm">
                No clients yet. <Link to="/app/clients" className="text-[#4b5320]">Add your first client</Link>
              </div>
            )}
            {clients.map((c) => {
              const lift = (c.currentScore || 0) - (c.initialScore || 0);
              return (
                <Link key={c.id} to={`/app/clients/${c.id}`} className="flex items-center gap-4 px-6 py-4 hover:bg-stone-100/50 transition-colors">
                  <div className="w-10 h-10 bg-[#4b5320]/15 border border-[#4b5320]/30 text-[#4b5320] flex items-center justify-center rounded-sm font-semibold text-sm">
                    {c.avatar}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-stone-900 text-sm font-medium truncate">{c.name}</div>
                    <div className="text-xs text-stone-500 truncate">{c.email}</div>
                  </div>
                  <div className="text-right hidden md:block">
                    <div className="text-xs text-stone-500 uppercase tracking-wider">Score</div>
                    <div className="font-mono text-stone-900 text-sm">
                      {c.initialScore || '—'} <span className="text-stone-400">→</span> <span className="text-[#4b5320]">{c.currentScore || '—'}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-stone-500 uppercase tracking-wider">Lift</div>
                    <div className={`font-mono text-sm ${lift > 0 ? 'text-[#7fb685]' : 'text-stone-500'}`}>+{lift}</div>
                  </div>
                  <StatusBadge status={c.status} />
                </Link>
              );
            })}
          </div>
        </div>

        <div className="border border-stone-200/80 bg-[#ffffff] rounded-sm">
          <div className="p-6 border-b border-stone-200/80">
            <div className="text-xs uppercase tracking-widest text-[#4b5320] mb-1">Field Report</div>
            <h2 className="font-display text-xl text-stone-900">Recent Activity</h2>
          </div>
          <div className="divide-y divide-stone-200/80">
            {activity.length === 0 && (
              <div className="p-10 text-center text-stone-500 text-sm">No activity yet.</div>
            )}
            {activity.map((a) => {
              const Icon = iconMap[a.type] || BarChart3;
              const ts = a.createdAt ? new Date(a.createdAt).toLocaleString() : '';
              return (
                <div key={a.id} className="px-6 py-4 flex gap-3">
                  <div className="w-8 h-8 bg-stone-100 border border-stone-200 flex items-center justify-center rounded-sm flex-shrink-0">
                    <Icon className="w-3.5 h-3.5 text-[#4b5320]" strokeWidth={1.8} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm text-stone-800">{a.message}</div>
                    <div className="text-xs text-stone-500 mt-0.5">{a.clientName} · {ts}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
